import cntl from 'cntl'
import Link from 'components/Link'
import useiCloudReady from '../../hooks/useiCloudReady'
import React, { useState, useEffect } from 'react'
import { QuickContact, FormField } from 'components/QuickContact'

const sizes = {
    xs: 'text-sm font-bold px-4 py-2',
    sm: 'text-sm font-bold px-6 py-2.5',
    md: 'text-base font-bold px-5 py-3',
    lg: 'text-base font-bold px-6 py-3',
}

const primary = cntl`
    bg-red
    border-red
    dark:bg-dark
    text-white
    dark:text-white
    hover:text-white
    hover:dark:text-white
    hover:bg-red-hover
    hover:border-red-hover
    active:bg-red-active
    active:border-red-active
    dark:primary-dark
`

const secondary = cntl`
    bg-white
    border-white
    text-primary
    hover:text-primary
`

const outline = cntl`
    bg-white
    border-black/10
    hover:border-black/30
    border
    text-primary
    hover:text-primary
`

const custom = cntl`
    bg-dark
    text-primary-dark
    hover:text-primary-dark
`

const containerTypes = {
    primary: cntl`
        bg-button-shadow
        dark:bg-button-shadow-dark
    `,
    secondary: cntl`
        bg-orange
        dark:bg-button-secondary-shadow-dark
        dark:border-button-secondary-dark
    `,
    outline: cntl`
        bg-yellow/50
        border-dark/30
        dark:bg-white/10
        dark:border-white/20
    `,
    custom: cntl`
        bg-white/20
        border-white/30
    `,
}

const containerSizes = {
    sm: cntl`border-[1.5px] relative top-[1px] rounded-[6px]`,
    md: cntl`border-[1.5px] relative top-[2px] rounded-[6px]`,
    lg: cntl`border-[1.5px] relative top-[2px] rounded-[8px]`,
}

export type ButtonType = keyof typeof buttonTypes
export type SizeType = keyof typeof sizes
export type ContainerType = keyof typeof containerTypes

export const container = (type: ButtonType = 'primary', size: SizeType = 'lg', width = 'auto') => cntl`
    ${containerTypes[type]}
    ${containerSizes[size]}
    w-${width}
    text-primary
    inline-block
    border-button
    text-center
    group
    disabled:opacity-50
    disabled:cursor-not-allowed
`

export const child = (type: ButtonType = 'primary', width = 'auto', className = '', size: SizeType = 'lg') =>
    button(type, width, `block active:transition-all active:duration-100 select-none ${className}`, size)

const buttonTypes = {
    primary,
    secondary,
    outline,
    custom: '',
}

export const button = (
    type: keyof typeof buttonTypes = 'primary',
    width = 'auto',
    className = '',
    size: keyof typeof sizes = 'lg'
) => cntl`
    text-center
    select-none
    rounded-sm
    inline-block
    cta
    button-shadow
    shadow-xl
    relative
    active:top-[0.5px]
    active:scale-[.98]
    w-${width}
    ${buttonTypes[type] || ''}
    ${sizes[size]}
    ${className}
`

export interface CTAPropsType {
    type?: ButtonType
    width?: string
    size?: SizeType
    href?: string
    to?: string
    onClick?: () => void
    children?: React.ReactNode
    className?: string
    external?: boolean
    externalNoIcon?: boolean
    direction?: 'rtl' | 'ltr' | 'auto'
    state?: any
    event?: any
    openQuickContact?: boolean
    isSecured?: boolean | string
    securedFormId?: string
    form?: {
        title?: string
        subtitle?: string
        formUrl?: string
        formFields?: Array<FormField>
        confirmationTitle?: {
            title: string
            subtitle: string
            description: string
        }
        confirmationMessageCTA?: {
            title: string
            url: string
        }
        useSampleConfig?: boolean
    }
}

export interface TrackedCTAPropsType extends CTAPropsType {
    event: {
        name: string
        [key: string]: any
    }
}

export const TrackedCTA = ({ event: { name: eventName, ...event }, ...props }: TrackedCTAPropsType): JSX.Element => {
    const icloudready = useiCloudReady()
    return (
        <CallToAction
            {...props}
            onClick={() => {
                icloudready?.capture(eventName, event)
                props.onClick && props.onClick()
            }}
        />
    )
}

export const CallToAction = ({
    type = 'primary',
    width = 'auto',
    size = 'lg',
    href,
    to,
    direction,
    onClick,
    children,
    className,
    external,
    externalNoIcon,
    state = {},
    event,
    openQuickContact,
    isSecured = false,
    securedFormId,
    form,
}: CTAPropsType): JSX.Element => {
    const [showQuickContact, setShowQuickContact] = useState(false)
    const [hasCompletedForm, setHasCompletedForm] = useState(false)
    const url = to || href

    // Convert isSecured to boolean
    const isSecuredBool = isSecured === true || isSecured === 'true'

    // Generate a unique form ID if not provided
    const generatedFormId = React.useMemo(() => {
        if (securedFormId) return securedFormId

        // Get the last part of the URL path
        const urlPath = url || 'button'
        const lastPathSegment = urlPath.split('/').filter(Boolean).pop() || 'default'

        // Create a shorter ID using just the last path segment
        return `secured-${lastPathSegment.replace(/[^a-zA-Z0-9]/g, '-')}`
    }, [securedFormId, url])

    useEffect(() => {
        if (isSecuredBool) {
            const formStatus = localStorage.getItem(`secured-form-${generatedFormId}`)
            setHasCompletedForm(formStatus === 'completed')
        }
    }, [isSecuredBool, generatedFormId])

    const handleClick = (e: React.MouseEvent) => {
        if (isSecuredBool && !hasCompletedForm) {
            e.preventDefault()
            setShowQuickContact(true)
            return
        }

        if (openQuickContact) {
            e.preventDefault()
            setShowQuickContact(true)
            return
        }

        onClick?.()
    }

    const handleFormSubmitSuccess = () => {
        if (isSecuredBool) {
            localStorage.setItem(`secured-form-${generatedFormId}`, 'completed')
            setHasCompletedForm(true)

            // Close the form
            setShowQuickContact(false)

            // If there's a URL, navigate to it
            if (url) {
                // Small delay to ensure the form is closed
                setTimeout(() => {
                    if (external) {
                        window.location.href = url
                    } else {
                        // Assuming you're using a router that exposes navigate
                        // If you're using a different router, this part needs to be adjusted
                        window.location.href = url
                    }
                }, 100)
            }
        } else {
            setShowQuickContact(false)
        }
    }

    return (
        <>
            {url ? (
                <Link
                    state={state}
                    dir={direction}
                    external={external}
                    externalNoIcon={externalNoIcon}
                    className={button(type, width, className, size)}
                    onClick={handleClick}
                    to={url}
                    event={event}
                >
                    {children}
                </Link>
            ) : (
                <button className={button(type, width, className, size)} onClick={handleClick}>
                    {children}
                </button>
            )}

            {(openQuickContact || (isSecuredBool && !hasCompletedForm)) && (
                <QuickContact
                    {...form}
                    isOpen={showQuickContact}
                    onClose={() => setShowQuickContact(false)}
                    direction={direction}
                    onSubmitSuccess={handleFormSubmitSuccess}
                />
            )}
        </>
    )
}
